/*
 * The client's Messages choices (phase 6; GUARDRAILS G27): read from
 * `/api/current`, saved through the CMS's `PUT /api/client/messages`.
 *
 * Held in memory only, for the signed-in record (its id and token), and
 * dropped at sign-out (G1). Pages that already read `/api/current` hand
 * their answer in (`rememberMessages`), so the booked page and the moved
 * page say "by email" without a second read, and a save shows everywhere at
 * once. A record switched by a code (G13) is a new key, read afresh.
 */
import { CMS_BASE_URL } from '../config/api.config';
import { getCMSClientId, getCMSJWT } from './auth.service';
import { emailOf, messagesFrom, messagesRefusal, type Messages, type MessagesBody } from '../utils/messages';

export interface MessagesRead {
  messages: Messages;
  email: string | null;
}

/** null: the API doesn't send the choices (or couldn't be read): nothing shows, today's words stand. */
type Known = MessagesRead | null;

let kept: { key: string; read: Known } | null = null;
let inflight: { key: string; promise: Promise<Known> } | null = null;
const listeners = new Set<() => void>();

const keyNow = (): string | null => {
  const jwt = getCMSJWT();
  return jwt ? `${getCMSClientId() ?? ''}:${jwt}` : null;
};

const tell = () => listeners.forEach(fn => fn());

const readOf = (raw: unknown): Known => {
  const messages = messagesFrom(raw);
  return messages ? { messages, email: emailOf(raw) } : null;
};

/** What is known for the signed-in record; undefined while nothing is. */
export const peekMessages = (): Known | undefined => {
  const key = keyNow();
  if (!key) return null;
  return kept?.key === key ? kept.read : undefined;
};

/** Keep what a read of `/api/current` made elsewhere carries. */
export const rememberMessages = (raw: unknown): void => {
  const key = keyNow();
  if (!key || !raw || typeof raw !== 'object') return;
  kept = { key, read: readOf(raw) };
  tell();
};

/** The signed-in record's choices, read once per record. */
export const readMessages = async (): Promise<Known> => {
  const key = keyNow();
  if (!key) return null;
  if (kept?.key === key) return kept.read;
  if (inflight?.key === key) return inflight.promise;
  const jwt = getCMSJWT();
  const promise = (async (): Promise<Known> => {
    let read: Known = null;
    try {
      const response = await fetch(`${CMS_BASE_URL}/api/current`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' },
        cache: 'no-store',
      });
      if (response.ok) read = readOf(await response.json());
    } catch {
      /* unknown: nothing shows, today's words stand */
    }
    if (inflight?.key === key) inflight = null;
    if (keyNow() === key && kept?.key !== key) {
      kept = { key, read };
      tell();
    }
    return keyNow() === key && kept?.key === key ? kept.read : read;
  })();
  inflight = { key, promise };
  return promise;
};

export const subscribeMessages = (fn: () => void): (() => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

/** Sign-out (G1): nothing of the last person stays. */
export const forgetMessages = (): void => {
  kept = null;
  inflight = null;
  tell();
};

/** A save the server refused, in its words: the sheet stays open and says so. */
export class MessagesRefusedError extends Error {
  constructor(
    message: string,
    readonly code: string | null
  ) {
    super(message);
    this.name = 'MessagesRefusedError';
  }
}

export const isMessagesRefused = (error: unknown): error is MessagesRefusedError =>
  (error as { name?: unknown } | null)?.name === 'MessagesRefusedError';

/**
 * Done (MKT 16): only the changed choices go, and no client id; the CMS
 * takes the client from the token. Answers what the server now holds.
 */
export const saveMessages = async (body: MessagesBody, sent: Messages): Promise<Messages> => {
  const jwt = getCMSJWT();
  if (!jwt) throw new Error('SESSION_EXPIRED');
  const response = await fetch(`${CMS_BASE_URL}/api/client/messages`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    const refusal = response.status === 409 || response.status === 400 ? messagesRefusal(text) : null;
    throw new MessagesRefusedError(refusal?.error ?? 'That didn’t save. Please try again.', refusal?.code ?? null);
  }
  const answer = await response.json().catch(() => null);
  const saved = messagesFrom(answer) ?? sent;
  const key = keyNow();
  if (key && kept?.key === key && kept.read) {
    kept = { key, read: { ...kept.read, messages: saved } };
    tell();
  }
  return saved;
};
